import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

const features = [
  {
    icon: 'ri-book-open-line',
    title: 'Browse Stories',
    description:
      'Dive into hundreds of original horror tales spanning every dark subgenre, from slow-burn psychological dread to visceral supernatural terror.',
    link: '/stories',
    linkLabel: 'Explore Stories',
  },
  {
    icon: 'ri-trophy-line',
    title: 'Top Ranked',
    description:
      'Discover the most-liked, most-read, and highest-rated stories as voted by our community. The cream of the darkness rises to the top.',
    link: '/top-ranked',
    linkLabel: 'See Rankings',
  },
  {
    icon: 'ri-ghost-line',
    title: 'The Dark Congregation',
    description:
      'Join our community forums to discuss your favourite stories, share fan art, debate theories, and connect with fellow horror enthusiasts.',
    link: '/community',
    linkLabel: 'Join the Community',
  },
  {
    icon: 'ri-quill-pen-line',
    title: 'Submit Your Story',
    description:
      'Have a tale that keeps people up at night? Submit your original horror fiction and let the world tremble at your words.',
    link: '/submit',
    linkLabel: 'Start Writing',
  },
];

const teamValues = [
  {
    icon: 'ri-eye-line',
    title: 'Authentic Horror',
    description: 'We celebrate stories that genuinely unsettle, no jump-scare gimmicks, just pure, crafted dread.',
  },
  {
    icon: 'ri-heart-line',
    title: 'Community First',
    description: 'Every feature we build is shaped by our readers and writers. This platform belongs to the darkness we share.',
  },
  {
    icon: 'ri-shield-line',
    title: 'Safe Creative Space',
    description: 'We foster a respectful environment where writers can explore dark themes without fear of judgment.',
  },
];

const AboutPage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="About Sinister Files | Horror Fiction Community Platform"
        description="Learn about Sinister Files – the premier community for horror fiction readers and writers. Discover our mission to celebrate authentic dark storytelling, creepypasta, and supernatural horror."
        canonical="https://sinisterfiles.com/about"
      />
      <Navbar scrolled={scrolled} />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/128271b07e377aa895142879dd2cdb8f.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#0a0a0a]" />
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <p className="text-red-500 text-sm font-['Cinzel'] tracking-[0.3em] uppercase mb-4 animate-pulse">
            Est. in the Shadows
          </p>
          <h1 className="text-5xl md:text-7xl font-bold text-white font-['Cinzel'] mb-6 leading-tight">
            About <span className="text-red-600">Sinister Files</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl font-['Crimson_Text'] italic leading-relaxed">
            A sanctuary for those who find beauty in the dark, where horror fiction lives, breathes, and haunts.
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-red-500 text-xs font-['Cinzel'] tracking-[0.3em] uppercase mb-3">Our Story</p>
              <h2 className="text-4xl md:text-5xl font-bold text-white font-['Cinzel'] mb-6 leading-tight">
                Who We Are
              </h2>
              <div className="w-16 h-0.5 bg-red-600 mb-8" />
              <p className="text-gray-300 font-['Crimson_Text'] text-lg leading-relaxed mb-6">
                <strong className="text-white">Sinister Files</strong> was born from a simple belief: the best horror stories deserve a home worthy of their darkness. We are a community-driven platform built by horror enthusiasts, for horror enthusiasts.
              </p>
              <p className="text-gray-400 font-['Crimson_Text'] text-lg leading-relaxed mb-6">
                From psychological thrillers that unravel the mind to supernatural tales that defy explanation, we curate and celebrate original horror fiction in all its terrifying forms. Our writers are storytellers who dare to go where others won't... and our readers are brave enough to follow.
              </p>
              <p className="text-gray-400 font-['Crimson_Text'] text-lg leading-relaxed">
                Whether you're here to read, write, or simply wander the dark corridors of our community, you've found your place among the shadows.
              </p>
            </div>
            <div className="relative">
              <div className="relative rounded-lg overflow-hidden border border-red-900/30">
                <img
                  src="https://readdy.ai/api/search-image?query=eerie%20candlelit%20writing%20desk%20with%20quill%20pen%20ink%20bottle%20parchment%20paper%20scattered%20notes%20dark%20gothic%20atmosphere%20warm%20amber%20candlelight%20casting%20long%20shadows%20on%20wooden%20desk%20antique%20books%20stacked%20beside%20mysterious%20and%20atmospheric%20horror%20aesthetic&width=700&height=500&seq=about-desk-01&orientation=landscape"
                  alt="The art of dark storytelling"
                  className="w-full h-80 object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border border-red-600/30 rounded-lg" />
              <div className="absolute -top-4 -right-4 w-16 h-16 border border-red-600/20 rounded-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-4 bg-[#0d0d0d] border-y border-red-900/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-red-500 text-xs font-['Cinzel'] tracking-[0.3em] uppercase mb-3">What Drives Us</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Cinzel']">Our Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamValues.map((val) => (
              <div
                key={val.title}
                className="bg-[#111] border border-red-900/20 rounded-lg p-8 hover:border-red-600/40 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-red-600/10 rounded-lg mb-5 group-hover:bg-red-600/20 transition-colors">
                  <i className={`${val.icon} text-red-500 text-2xl`} />
                </div>
                <h3 className="text-white font-bold font-['Cinzel'] text-lg mb-3">{val.title}</h3>
                <p className="text-gray-400 font-['Crimson_Text'] text-base leading-relaxed">{val.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-red-500 text-xs font-['Cinzel'] tracking-[0.3em] uppercase mb-3">Explore the Darkness</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white font-['Cinzel'] mb-4">What We Offer</h2>
            <p className="text-gray-400 font-['Crimson_Text'] text-lg max-w-xl mx-auto">
              Everything a horror fiction lover could need, all in one shadowy corner of the internet.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feat) => (
              <div
                key={feat.title}
                className="bg-[#0d0d0d] border border-red-900/20 rounded-lg p-8 hover:border-red-600/40 transition-all duration-300 group flex gap-6"
              >
                <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-red-600/10 rounded-lg group-hover:bg-red-600/20 transition-colors">
                  <i className={`${feat.icon} text-red-500 text-2xl`} />
                </div>
                <div>
                  <h3 className="text-white font-bold font-['Cinzel'] text-lg mb-2">{feat.title}</h3>
                  <p className="text-gray-400 font-['Crimson_Text'] text-base leading-relaxed mb-4">
                    {feat.description}
                  </p>
                  <Link
                    to={feat.link}
                    className="inline-flex items-center gap-2 text-red-500 hover:text-red-400 font-['Cinzel'] text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {feat.linkLabel}
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=dark%20misty%20forest%20at%20night%20with%20twisted%20bare%20trees%20silhouetted%20against%20deep%20crimson%20moonlit%20sky%20fog%20rolling%20across%20the%20ground%20eerie%20atmospheric%20horror%20aesthetic%20cinematic%20wide%20shot%20mysterious%20and%20haunting%20landscape&width=1440&height=600&seq=about-cta-01&orientation=landscape)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-black/70 to-[#0a0a0a]" />
        <div className="relative z-10 text-center max-w-2xl mx-auto">
          <i className="ri-skull-line text-red-600 text-5xl mb-6 block" />
          <h2 className="text-4xl md:text-5xl font-bold text-white font-['Cinzel'] mb-6 leading-tight">
            Join the Darkness
          </h2>
          <p className="text-gray-300 font-['Crimson_Text'] text-lg leading-relaxed mb-10">
            Create your free account and become part of a community that embraces the dark side of storytelling. Read, write, and connect... if you dare.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-['Cinzel'] font-bold rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Create Free Account
            </Link>
            <Link
              to="/stories"
              className="px-8 py-3 border border-red-600/50 hover:border-red-600 text-red-400 hover:text-red-300 font-['Cinzel'] font-bold rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Browse Stories First
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
